import React from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import PlayerHeatmap from './PlayerHeatmap';
import './PlayerDetailModal.css';

export default function PlayerDetailModal({ player, onClose }) {
  if (!player) return null;
  const p = player;
  const color = p.team === 'home' ? '#00d4ff' : '#7c3aed';

  return createPortal(<ModalContent p={p} color={color} onClose={onClose} />, document.body);
}

// Rendered via a portal straight into document.body — pages wrap their
// content in a `.fade-in` container whose entrance animation leaves a
// residual `transform` (fill-mode: forwards), which creates a CSS containing
// block for any `position: fixed` descendant. Without the portal this modal
// would be positioned relative to that container instead of the viewport.
function ModalContent({ p, color, onClose }) {

  const statEntries = [
    ['Touches', p.stats?.touches],
    ['Passes', (p.stats?.passesAttempted != null) ? `${p.stats?.passesCompleted ?? '–'}/${p.stats.passesAttempted}` : null],
    ['Pass Acc.', p.stats?.passAccuracy != null ? `${p.stats.passAccuracy}%` : null],
    ['Shots', (p.stats?.shots != null) ? `${p.stats?.shotsOnTarget ?? 0}/${p.stats.shots}` : null],
    ['Def. Actions', p.stats?.defensiveActions],
  ].filter(([, v]) => v != null);

  return (
    <div className="pdm-overlay" onClick={onClose}>
      <div className="pdm-card" onClick={e => e.stopPropagation()}>
        <button className="pdm-close" onClick={onClose}><X size={18} /></button>

        <div className="pdm-header">
          <div className="pdm-avatar" style={{ borderColor: color, color }}>{p.name?.[0] || '?'}</div>
          <div style={{ flex: 1 }}>
            <div className="pdm-name">{p.name}</div>
            <div className="pdm-meta">
              <span className={`badge ${p.team === 'home' ? 'badge-blue' : 'badge-purple'}`}>{p.teamName || (p.team === 'home' ? 'Home' : 'Away')}</span>
              <span className="pdm-position">{p.position}</span>
              {!p.numberLegible && <span className="pdm-descriptor-note">visual descriptor, not a read shirt number</span>}
            </div>
            {(p.homeTeam || p.awayTeam) && (
              <div className="pdm-match">From: {p.homeTeam} vs {p.awayTeam}{p.matchDate ? ` · ${p.matchDate}` : ''}</div>
            )}
          </div>
          <div className="pdm-rating" data-h={p.rating >= 8 ? 'high' : p.rating >= 6.5 ? 'mid' : 'low'}>
            {typeof p.rating === 'number' ? p.rating.toFixed(1) : p.rating}
          </div>
        </div>

        {p.statsConfidence && (
          <span
            className={`badge badge-${p.statsConfidence === 'high' ? 'green' : p.statsConfidence === 'medium' ? 'amber' : 'red'}`}
            style={{ marginBottom: 14, display: 'inline-block' }}
            title="How reliably this player could be tracked across the footage"
          >
            {p.statsConfidence} confidence
          </span>
        )}

        <div className="pdm-grid">
          <div>
            {statEntries.length > 0 && (
              <div className="pdm-stats">
                {statEntries.map(([label, val]) => (
                  <div key={label} className="pdm-stat">
                    <div className="pdm-stat__val">{val}</div>
                    <div className="pdm-stat__key">{label}</div>
                  </div>
                ))}
              </div>
            )}

            {p.keyActions?.length > 0 && (
              <div className="pdm-section">
                <div className="pdm-section__label">Key Actions</div>
                <div className="pdm-tags">{p.keyActions.map((a, i) => <span key={i} className="action-tag">{a}</span>)}</div>
              </div>
            )}

            {p.strengths?.length > 0 && (
              <div className="pdm-section">
                <div className="pdm-section__label">Strengths</div>
                <div className="pdm-tags">{p.strengths.map((s, i) => <span key={i} className="trait-tag trait-tag--green">{s}</span>)}</div>
              </div>
            )}

            {p.weaknesses?.length > 0 && (
              <div className="pdm-section">
                <div className="pdm-section__label">Areas to Improve</div>
                <div className="pdm-tags">{p.weaknesses.map((w, i) => <span key={i} className="trait-tag trait-tag--red">{w}</span>)}</div>
              </div>
            )}
          </div>

          <div className="pdm-heatmap-col">
            <div className="pdm-section__label">Zones of Play</div>
            <PlayerHeatmap zones={p.zones} color={color} />
          </div>
        </div>
      </div>
    </div>
  );
}
